import React, { useState, useEffect, useRef } from 'react';
import cipherLogo from '../assets/cipher-icon-only-transparent.png';
import { colors as theme } from '../theme';

const c = {
  bg: theme.bg, card: theme.surface1, cardDim: '#0F1530',
  border: 'rgba(255,255,255,0.08)', goldBorder: 'rgba(212,175,55,0.35)',
  dimBorder: 'rgba(255,255,255,0.1)', text: theme.text, muted: theme.textMuted,
  gold: theme.gold, success: theme.success, error: theme.danger, green: '#4A8F62',
};

const GoldCheck = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
    <circle cx="8" cy="8" r="8" fill="rgba(212,175,55,0.15)" />
    <path d="M5 8l2 2 4-4" stroke={theme.gold} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GreenCheck = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
    <circle cx="8" cy="8" r="8" fill="rgba(74,143,98,0.15)" />
    <path d="M5 8l2 2 4-4" stroke="#4A8F62" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const fitnessFeatures = [
  'Personalised training program built for you',
  'Log every workout with real-time guidance',
  'Track strength, volume & personal records',
  'Progress photos & monthly check-ins',
  'Direct coach feedback & form flags',
  'Works on phone, tablet & desktop',
];

const nutritionFeatures = [
  'AI-powered 7-day meal plans',
  'GLP-1 medication support & adaptation',
  'Up to 20 new meal plans and 100 swaps a month (5 and 10 a day)',
  'Monthly nutrition review & refresh',
];

const PRODUCTS = {
  fitness: {
    label: 'Cipher Fitness',
    plans: [
      { id: 'monthly', label: 'Monthly', price: '£14.99', sub: '/mo', billing: 'cancel anytime', saving: null, badge: null },
      { id: 'annual', label: 'Annual', price: '£149.99', sub: '/yr', billing: null, saving: '2 months free', badge: 'Best Value' },
    ],
    defaultPlan: 'monthly',
    cta: (plan, trialEligible) => (trialEligible === true ? 'Start Free Trial' : trialEligible === false ? 'Subscribe' : 'Continue')
      + ` — ${plan === 'annual' ? 'Annual' : 'Monthly'}`,
  },
  bundle: {
    label: 'Health Bundle',
    plans: [
      { id: 'bundle-monthly', label: 'Monthly', price: '£19.99', sub: '/mo', billing: 'cancel anytime', saving: null, badge: null },
      { id: 'bundle-annual', label: 'Annual', price: '£199.99', sub: '/yr', billing: null, saving: '2 months free', badge: 'Best Value' },
    ],
    defaultPlan: 'bundle-monthly',
    cta: (_plan, trialEligible) => `${trialEligible === true ? 'Start Free Trial' : trialEligible === false ? 'Subscribe' : 'Continue'} — Bundle`,
  },
};

const PaymentGate = ({ userId, email, name, onLogout }) => {
  // From 16 October 2026 Cipher offers new members the bundle only (Fitness + Nutrition), monthly or
  // yearly: the owner's decision. It opens on monthly. Clients already paying for Fitness alone keep
  // their subscription; it is just no longer offered here.
  const [product] = useState('bundle');
  const [selectedPlan, setSelectedPlan] = useState('bundle-monthly');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  // Checkout said this person already has a subscription (or a payment still being confirmed): the screen
  // offers the billing page instead of another try
  const [canManageBilling, setCanManageBilling] = useState(false);
  const [openingBilling, setOpeningBilling]     = useState(false);
  // On a phone the message appears above the button that was just tapped, off the screen: bring it into view
  const errorBox = useRef(null);
  useEffect(() => {
    if (error) errorBox.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [error]);

  // Nutrition tier detection
  const [nutritionTier, setNutritionTier]       = useState(null); // null=loading, 'none','free','monthly','annual','bundle'

  // Trial eligibility, from the server's one rule (api/lib/trialEligibility.js), the same rule checkout
  // uses: null while checking, 'unknown' if the check failed, else true or false. Nothing is promised
  // until the server has answered (plan 011 step 1): until 25 October 2026 this assumed a trial.
  const [trialEligible, setTrialEligible] = useState(null);

  // Promo code — redeems a complimentary month via /api/redeem-promo (no card).
  // Shown as a prominent always-open card near the top of the paywall so a
  // campaign user who can't scan the QR doesn't have to hunt for it.
  const [promoCode, setPromoCode]       = useState('');
  const [promoOpen, setPromoOpen]       = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError]     = useState('');
  const [promoDone, setPromoDone]       = useState(false);
  // True when they arrived via a ?promo= deep link — read synchronously so the
  // pricing UI never flashes before the auto-apply takes over.
  const [promoAuto] = useState(() => {
    try { return !!localStorage.getItem('cipher_promo_code'); } catch { return false; }
  });

  useEffect(() => {
    const checkNutritionTier = async () => {
      try {
        const { supabase } = await import('../supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { setNutritionTier('none'); return; }
        const res = await fetch('/api/get-nutrition-tier', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        });
        const data = await res.json();
        const tier = data.tier ?? 'none';
        setNutritionTier(tier);
        // Bundle already active — reload so loadUserProfile can grant dashboard access
        if (tier === 'bundle') {
          setTimeout(() => window.location.reload(), 2000);
        }
      } catch {
        setNutritionTier('none');
      }
    };
    checkNutritionTier();
  }, []);

  useEffect(() => {
    const checkTrialEligibility = async () => {
      try {
        const { supabase } = await import('../supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { setTrialEligible('unknown'); return; }
        const res = await fetch('/api/check-trial-eligibility', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        });
        const data = res.ok ? await res.json() : null;
        setTrialEligible(typeof data?.eligibleForTrial === 'boolean' ? data.eligibleForTrial : 'unknown');
      } catch {
        // Not answered: say nothing about a trial. Stripe's checkout page shows whether one applies.
        setTrialEligible('unknown');
      }
    };
    checkTrialEligibility();
  }, []);

  const handleRedeemPromo = async (codeArg) => {
    const code = String(typeof codeArg === 'string' ? codeArg : promoCode).trim();
    if (!code) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const { supabase } = await import('../supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Please log in again.');
      const res = await fetch('/api/redeem-promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      // A response came back (success or a definitive rejection) — never let a
      // stored deep-link code keep retrying on every paywall mount.
      try { localStorage.removeItem('cipher_promo_code'); } catch { /* ignore */ }
      if (!res.ok) throw new Error(data.error || 'Could not apply that code.');
      setPromoDone(true);
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setPromoError(err.message);
      setPromoLoading(false);
    }
  };

  // Deep-link auto-apply: if they arrived via a ?promo= link, open the box,
  // fill it, and submit once. Stored code is cleared by handleRedeemPromo.
  const promoAutoTried = useRef(false);
  useEffect(() => {
    if (promoAutoTried.current) return;
    let stored = null;
    try { stored = localStorage.getItem('cipher_promo_code'); } catch { /* ignore */ }
    if (!stored) return;
    promoAutoTried.current = true;
    setPromoCode(stored);
    handleRedeemPromo(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // The customer portal (api/create-portal-session.js): card, plan and invoices of the subscription they have
  const handleManageBilling = async () => {
    setOpeningBilling(true);
    try {
      const { supabase } = await import('../supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Your sign-in has expired. Please refresh the page and try again.');
      const res = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ userId }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || 'Could not open billing. Please try again.');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Could not open billing. Please try again.');
      setOpeningBilling(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    setError('');
    setCanManageBilling(false);
    try {
      // The server starts a checkout only for the person signed in (15 October 2026)
      const { supabase } = await import('../supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Your sign-in has expired. Please refresh the page and try again.');
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ userId, email, name, plan: selectedPlan }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'already_subscribed') setCanManageBilling(true);
        throw new Error(data.error || 'Something went wrong');
      }
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  const prod = PRODUCTS[product];
  const activePlan = prod.plans.find(p => p.id === selectedPlan) ?? prod.plans[0];
  // The price and when it is charged, said in words beside the button
  const perPeriod = activePlan.sub === '/yr' ? 'a year' : 'a month';
  const isBundle = product === 'bundle';

  // Arrived via a ?promo= link — show a quiet "applying" screen instead of the
  // price list while the auto-redeem runs. On failure (promoError set) we fall
  // through to the normal paywall below, which surfaces the error in the box.
  if (promoAuto && !promoError) {
    return (
      <div style={{ minHeight: '100dvh', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', fontFamily: "'Inter', sans-serif" }}>
        <div style={{ width: '100%', maxWidth: 360, textAlign: 'center' }}>
          <img src={cipherLogo} alt="Cipher" style={{ width: 72, height: 72, borderRadius: 14, display: 'block', margin: '0 auto 20px' }} />
          <div style={{ color: c.text, fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
            {promoDone ? "You're in" : 'Applying your free month…'}
          </div>
          <p style={{ color: c.muted, fontSize: 13, margin: 0, lineHeight: 1.5 }}>
            {promoDone ? 'Loading your platform…' : 'One moment while we set up your account.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', fontFamily: "'Inter', sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 460 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src={cipherLogo} alt="Cipher" style={{ width: 80, height: 80, borderRadius: 20, display: 'block', margin: '0 auto 14px' }} />
          <h1 style={{ color: c.text, fontSize: 20, fontWeight: 700, margin: '0 0 6px' }}>Unlock Cipher</h1>
          <p style={{ color: c.muted, fontSize: 14, margin: 0, lineHeight: 1.5 }}>
            Start your membership to access your personalised platform
          </p>
        </div>

        {/* Trial badge: only once the server says a trial applies; returning customers are charged at once */}
        {trialEligible === null && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <span style={{ display: 'inline-block', padding: '5px 14px', color: c.muted, fontSize: 12 }}>
              Checking your free trial…
            </span>
          </div>
        )}
        {trialEligible === true && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <span style={{ display: 'inline-block', background: 'rgba(52,199,89,0.12)', border: '1px solid rgba(52,199,89,0.35)', borderRadius: 20, padding: '5px 14px', color: c.success, fontSize: 12, fontWeight: 600 }}>
              7-day free trial — no charge today
            </span>
          </div>
        )}

        {/* A Nutrition-only subscription can no longer be upgraded here: Nutrition is not sold on its own
            and payments are one system (owner, 24 October 2026; nobody had one). Such a member chooses the
            bundle below like anyone else. */}

        {/* If nutrition bundle — skip the gate entirely */}
        {nutritionTier === 'bundle' && (
          <div style={{ background: 'rgba(74,143,98,0.1)', border: '1px solid rgba(74,143,98,0.35)', borderRadius: 20, padding: 20, marginBottom: 20, textAlign: 'center' }}>
            <div style={{ color: c.green, fontSize: 24, marginBottom: 8 }}>✓</div>
            <div style={{ color: c.text, fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Bundle Active</div>
            <div style={{ color: c.muted, fontSize: 13 }}>Your Nutrition Bundle subscription includes Cipher Fitness. Loading your programme…</div>
          </div>
        )}

        {/* Hide subscribe UI entirely when bundle is already active */}
        {nutritionTier === 'bundle' ? null : <>

        {/* One product: the bundle */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ color: c.green, fontSize: 15, fontWeight: 700 }}>✦ Cipher Health Bundle</div>
          <div style={{ color: c.muted, fontSize: 13, marginTop: 2 }}>Fitness + Nutrition, one membership</div>
        </div>

        {/* Plan selector */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          {prod.plans.map(plan => {
            const active = selectedPlan === plan.id;
            const borderColor = active ? (isBundle ? 'rgba(74,143,98,0.5)' : c.goldBorder) : c.dimBorder;
            const glowColor = active ? (isBundle ? 'rgba(74,143,98,0.08)' : 'rgba(212,175,55,0.06)') : 'transparent';
            const labelColor = active ? (isBundle ? c.green : c.gold) : c.muted;
            return (
              <button key={plan.id} onClick={() => setSelectedPlan(plan.id)} style={{
                flex: 1, padding: '14px 10px', borderRadius: 14, border: `1.5px solid ${borderColor}`,
                background: active ? c.card : c.cardDim, cursor: 'pointer', fontFamily: 'inherit',
                textAlign: 'center', transition: 'all 0.2s', position: 'relative', outline: 'none',
                boxShadow: active ? `0 0 20px ${glowColor}` : 'none',
              }}>
                {plan.badge && (
                  <div style={{
                    position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)',
                    background: isBundle ? `linear-gradient(135deg, ${c.green}, #3a7250)` : `linear-gradient(135deg, ${c.gold}, #C9A84C)`,
                    borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 700,
                    color: isBundle ? theme.text : '#000', whiteSpace: 'nowrap',
                  }}>{plan.badge}</div>
                )}
                <div style={{ color: labelColor, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5, marginTop: plan.badge ? 4 : 0 }}>
                  {plan.label}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                  <span style={{ color: active ? c.text : c.muted, fontSize: 24, fontWeight: 700, lineHeight: 1 }}>{plan.price}</span>
                  <span style={{ color: c.muted, fontSize: 11, marginLeft: 2 }}>{plan.sub}</span>
                </div>
                {plan.billing && <div style={{ color: c.muted, fontSize: 11, marginTop: 3 }}>{plan.billing}</div>}
                {plan.saving && <div style={{ color: c.success, fontSize: 11, fontWeight: 600, marginTop: 3 }}>{plan.saving}</div>}
              </button>
            );
          })}
        </div>

        {/* Features card */}
        <div style={{ background: c.card, borderRadius: 20, border: `1px solid ${isBundle ? 'rgba(74,143,98,0.3)' : c.goldBorder}`, padding: '22px', marginBottom: 14, boxShadow: `0 0 40px ${isBundle ? 'rgba(74,143,98,0.05)' : 'rgba(212,175,55,0.05)'}` }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: isBundle ? 16 : 22 }}>
            {fitnessFeatures.map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <GoldCheck />
                <span style={{ color: c.text, fontSize: 13, lineHeight: 1.4 }}>{f}</span>
              </div>
            ))}
          </div>

          {isBundle && (
            <>
              <div style={{ height: 1, background: 'rgba(74,143,98,0.2)', marginBottom: 14 }} />
              <div style={{ color: c.green, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
                + Cipher Nutrition
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
                {nutritionFeatures.map(f => (
                  <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <GreenCheck />
                    <span style={{ color: c.text, fontSize: 13, lineHeight: 1.4 }}>{f}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {error && (
            <div ref={errorBox} role="alert" style={{ background: 'rgba(255,59,48,0.1)', border: '1px solid rgba(255,59,48,0.3)', borderRadius: 8, padding: '10px 14px', color: c.error, fontSize: 13, marginBottom: 14 }}>
              {error}
              {canManageBilling && (
                <button onClick={handleManageBilling} disabled={openingBilling} style={{
                  display: 'block', width: '100%', marginTop: 10, padding: '12px', borderRadius: 10,
                  border: `1px solid ${c.gold}`, background: 'transparent', color: c.gold,
                  fontSize: 14, fontWeight: 600, cursor: openingBilling ? 'default' : 'pointer',
                }}>{openingBilling ? 'Opening billing…' : 'Manage billing'}</button>
              )}
            </div>
          )}

          <button onClick={handleSubscribe} disabled={loading} style={{
            width: '100%', padding: '15px', borderRadius: 14, border: 'none',
            background: loading ? 'rgba(212,175,55,0.4)' :
              isBundle ? `linear-gradient(135deg, ${c.green}, #3a7250)` :
              `linear-gradient(135deg, ${c.gold}, #C9A84C)`,
            color: isBundle ? theme.text : '#000',
            fontWeight: 700, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>
            {loading ? 'Redirecting to checkout…' : prod.cta(selectedPlan, trialEligible)}
          </button>

          <p style={{ color: theme.text, fontSize: 13, fontWeight: 600, textAlign: 'center', margin: '10px 0 0', lineHeight: 1.5 }}>
            {trialEligible === true
              ? `Free for 7 days, then ${activePlan.price} ${perPeriod}. Cancel anytime.`
              : trialEligible === false
              ? `${activePlan.price} ${perPeriod}, charged today. Cancel anytime.`
              : `${activePlan.price} ${perPeriod}. Checkout shows whether your free trial applies. Cancel anytime.`}
          </p>
          <p style={{ color: c.muted, fontSize: 11, textAlign: 'center', margin: '4px 0 0', lineHeight: 1.5 }}>
            Secured by Stripe
          </p>
        </div>

        </>}

        {/* Promo code — under the plans since 14 October 2026: the plans come first, and a person
            with a code taps "Have a promo code?" to open the box. A ?promo= link still applies itself
            (see promoAuto above). Redeems a free month via /api/redeem-promo, no card required. */}
        {nutritionTier !== 'bundle' && !promoOpen && !promoDone && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <button type="button" onClick={() => setPromoOpen(true)} style={{
              background: 'none', border: 'none', padding: '8px', color: c.gold, fontSize: 14, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline',
            }}>
              Have a promo code?
            </button>
          </div>
        )}
        {nutritionTier !== 'bundle' && (promoOpen || promoDone) && (
          <div style={{
            background: c.card, border: `1.5px solid ${c.goldBorder}`, borderRadius: 14,
            padding: 18, marginBottom: 20, boxShadow: '0 0 40px rgba(212,175,55,0.06)',
          }}>
            {promoDone ? (
              <div style={{ color: c.success, fontSize: 14, fontWeight: 600, textAlign: 'center' }}>
                ✓ Code applied — loading your platform…
              </div>
            ) : (
              <>
                <div style={{ color: c.gold, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
                  Have a promo code?
                </div>
                <p style={{ color: c.muted, fontSize: 12, margin: '0 0 12px', lineHeight: 1.5 }}>
                  Redeem it for a free month of Fitness + Nutrition — no card needed.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && promoCode.trim() && !promoLoading) handleRedeemPromo(); }}
                    placeholder="Enter code"
                    autoFocus
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    style={{ flex: 1, minWidth: 0, background: c.cardDim, border: `1px solid ${c.dimBorder}`, borderRadius: 8, padding: '12px', color: c.text, fontSize: 15, fontFamily: 'inherit', textTransform: 'uppercase', outline: 'none' }}
                  />
                  <button onClick={() => handleRedeemPromo()} disabled={promoLoading || !promoCode.trim()} style={{
                    padding: '12px 20px', borderRadius: 8, border: 'none', whiteSpace: 'nowrap',
                    background: (promoLoading || !promoCode.trim()) ? 'rgba(212,175,55,0.4)' : `linear-gradient(135deg, ${c.gold}, #C9A84C)`,
                    color: '#000', fontWeight: 700, fontSize: 14, cursor: (promoLoading || !promoCode.trim()) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  }}>
                    {promoLoading ? '…' : 'Apply'}
                  </button>
                </div>
                {promoError && (
                  <div style={{ color: c.error, fontSize: 12, marginTop: 8, lineHeight: 1.4 }}>{promoError}</div>
                )}
              </>
            )}
          </div>
        )}

        <div style={{ textAlign: 'center' }}>
          <button onClick={onLogout} style={{ background: 'none', border: 'none', color: c.muted, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentGate;
